import { useState, useEffect } from 'react';
import { inject } from 'mobx-react';
import { Modal, Row, Col, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import TimePickerInput from '~/components/TimePicker';
import Button from '~/components/CustomButton/CustomButton.jsx';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '~/utils/timeCalc.js';
import { timeFormat, totalTimeFormat } from '~/utils/dateFormat';
import { getCompanyOvertimeTypes } from '~/utils/commonMethods';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

const UrgentCallModal = (props) => {
    const { t, commonStore, urgentCallsStore, userId, forDate, handleUrgentCallModal, router } = props;
    const overtimeTypes = getCompanyOvertimeTypes(commonStore.config.client, commonStore.config.overtimes, false);
    const [urgentCallDetails, setUrgentCallDetails] = useState({});
    const [loading, setLoading] = useState(true);

    const dateTimeRules = commonStore.getDateTimeRules();

    async function loadData() {
        try {
            const data = await urgentCallsStore.loadEmployeeUrgentCallForDate(userId, forDate);
            setUrgentCallDetails(data);
            setLoading(false);
        } catch (error) {
            console.error({ error })
            commonStore.addNotification(t(error || 'Something went wrong'), null, 'error');
            setLoading(false);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    function handleTimeInputChange(value) {
        setUrgentCallDetails({ ...urgentCallDetails, hours: value });
    }

    async function handleSave() {
        try {
            if (!urgentCallDetails.hours) return commonStore.addNotification(t('Please enter hours'), null, 'warning');
            await urgentCallsStore.updateCustomerCareHours({ userId, forDate, hours: urgentCallDetails.hours });
            commonStore.addNotification(t('Saved'), null, 'success');
            loadData();
        } catch (error) {
            console.error({ error })
            commonStore.addNotification(t(error || 'Something went wrong'), null, 'error');
            setLoading(false);
        }
    }

    function handleActionButtons(action) {
        if (action === 'save') {
            setLoading(true);
            handleSave();
        } else {
            router.navigate("/admin/schedule", {
                state:
                {
                    showUrgentCallDetails: true,
                    userId: userId,
                    forDate: forDate,
                    weekNumber: urgentCallDetails.week_number
                }
            });
        }
    }

    function renderOvertimeList() {
        const { timesheet_configs } = urgentCallDetails?.data || [];
        if (!timesheet_configs?.length)
            return null;

        return (
            <div className="mt-2" style={{ width: '100%' }}>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>{t('Overtime')}</th>
                            <th>{t('Time Range')}</th>
                            <th>{t('Hours')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {timesheet_configs.map((overtime, index) => (
                            <tr key={index}>
                                <td>{overtimeTypes[overtime.overtime_code]?.name || 'Overtime'}</td>
                                <td className="text-muted">
                                    {timeFormat(overtime.from, dateTimeRules)} - {timeFormat(overtime.to, dateTimeRules)}
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {totalTimeFormat(overtime.min_hours_in_mins, dateTimeRules)}&nbsp;{t('Hours')}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (loading) return <LoadingSpinner />

    return (
        <Modal size="md" show={true} onHide={() => handleUrgentCallModal(false)}>
            <Modal.Header>
                <Modal.Title className="w-100 text-center m-0">
                    {t('Urgent work')}
                </Modal.Title>
                <img
                    src={CloseModalIcon}
                    className="cursor-pointer"
                    onClick={() => handleUrgentCallModal(false)}
                    alt={'cross-Icon'}
                />
            </Modal.Header>
            <Modal.Body style={{ minHeight: '300px' }}>
                <Container>
                    <Row className='my-2 align-items-center'>
                        <Col xs={4}>
                            <span className='fw-bold font-size-15' style={{ color: '#2550AC' }}>Type</span>
                        </Col>
                        <Col xs={8}>
                            <span className='fw-bold font-size-12'>{t('Urgent work')}-{urgentCallDetails.is_field_operative ? t("Field Operative") : t("Customer Care")}</span>
                        </Col>
                    </Row>
                    {!urgentCallDetails.is_field_operative &&
                        <Row className='mb-2 align-items-center'>
                            <Col xs={4}>
                                <span className='fw-bold font-size-15' style={{ color: '#2550AC' }}>{t("Hours")}</span>
                            </Col>
                            <Col xs={8}>
                                <TimePickerInput
                                    value={minutesToTimeInput(urgentCallDetails.hours)}
                                    onChange={(value) => handleTimeInputChange(getMinutesFromTimeInput(value))}
                                    format="HH:mm"
                                    className="worklog-input"
                                />
                            </Col>
                        </Row>
                    }
                    <Row>
                        <span className='fw-bold font-size-15' style={{ color: '#2550AC' }}>{t("Overtime Configuration")}</span>
                    </Row>
                    <Row>{renderOvertimeList()}</Row>
                </Container>
            </Modal.Body>
            {!urgentCallDetails.is_field_operative &&
                <Modal.Footer>
                    <OverlayTrigger
                        key="urgent-call"
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip-urgent">
                                {urgentCallDetails.int_details && urgentCallDetails.int_details.synced ?
                                    t("Synced with payroll, please reset and try again!") : t("Save")}
                            </Tooltip>
                        }
                    >
                        <Button wd fill
                            disabled={urgentCallDetails.int_details && urgentCallDetails.int_details.synced}
                            onClick={() => handleActionButtons('save')}>
                            {t('Save')}
                        </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                        key="urgent-call-redirect"
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip-urgent">
                                {t("Redirect to Urgent Work List")}
                            </Tooltip>
                        }
                    >
                        <Button wd
                            onClick={() => handleActionButtons('redirect')}>
                            {t('View')} {t('All')}
                        </Button>
                    </OverlayTrigger>
                </Modal.Footer>
            }
        </Modal>
    )
}

export default inject('commonStore', 'urgentCallsStore')(withLocalization(applicationRouter(UrgentCallModal)));